import React, {memo, useCallback, useState} from 'react';
import Dropzone from 'react-dropzone';
import {useDispatch, useSelector} from 'react-redux';
import {requestOK} from 'redux-thunk-kit';
import {AppDispatch} from '~/redux/root-store';
import {RootState} from '~/redux/root-reducer';
import {UiImage} from '~/components/elements/UiImage';
import {uploadFile} from '~/redux/apps/thunk';
import {UiIcon} from '~/components/elements/UiIcon';
import {FiUpload} from 'react-icons/all';
import {Row, UiBox, UiCenter, UiSpinner, UiText} from '~/components/elements/index';
import {CenterProps} from '@chakra-ui/react';
import {useNotification} from '~/hooks';

export interface UploadResponse {
  fileName: string;
  fileType: string;
  accessUrl: string;
  uid: string;
  localUrl: string;
}

type AcceptType = 'VIDEO' | 'PDF' | 'IMAGE' | 'AUDIO';

interface Props {
  type: string;
  placeholder?: string | null;
  contentProps?: CenterProps;
  onSuccess: (arg: UploadResponse[]) => any;
  onRemove?: (f: UploadResponse) => void;
  acceptType?: AcceptType[];
  multiple?: boolean;
  preview?: boolean;
  limit?: number;
  endpoint?: string;
  useLocalUrl?: boolean;
}

const getAcceptFileTypes = (types: AcceptType[]) => {
  const map = {
    VIDEO: 'video/*',
    AUDIO: 'audio/*',
    IMAGE: 'image/*',
    PDF: 'application/pdf',
  };
  return types.map(type => map[type]);
};

const maxSize = 30485760; // max file size 10MB

const defaultContentProps = {py: 8, px: 5};
export const AppDropzone = memo(function AppDropzone({
  type,
  onSuccess,
  preview = true,
  multiple = false,
  acceptType = ['PDF', 'IMAGE', 'VIDEO'],
  placeholder = 'Drag and Drop / click to select file',
  contentProps = {},
  limit = 1,
  endpoint,
  onRemove,
  useLocalUrl = false,
}: Props) {
  const dispatch: AppDispatch = useDispatch();
  const {addMessage} = useNotification(dispatch);
  const {loading} = useSelector((state: RootState) => state.ui[uploadFile.typePrefix] || {});

  const [uploadedFiles, setUploadedFiles] = useState<UploadResponse[]>([]);
  const [errorMessage, setErrorMessage] = useState('');

  const onDrop = useCallback(
    async acceptedFiles => {
      if (!acceptedFiles || acceptedFiles.length === 0) {
        return;
      }

      if (acceptedFiles.length > limit) {
        setErrorMessage(`You can upload only ${limit} files`);
        return;
      }

      let res: UploadResponse[] = [];

      for await (const file of acceptedFiles) {
        if (file.size > maxSize) {
          addMessage('File is too large. Please upload file under 30MB', 'error');
          return;
        }
        const resAction = await dispatch(
          uploadFile({
            file,
            fileName: file.name,
            type,
            endpoint,
          }),
        );
        if (requestOK(resAction, uploadFile)) {
          if (resAction?.payload && 'accessUrl' in resAction?.payload && 'uid' in resAction?.payload) {
            // @ts-ignore
            res.push({
              fileName: file.name,
              fileType: file.type,
              accessUrl: resAction?.payload?.accessUrl,
              uid: resAction?.payload?.uid,
              localUrl: URL.createObjectURL(file),
            });
          }
        }
      }
      res = multiple ? [...(uploadedFiles || []), ...res] : res;
      setUploadedFiles(res);
      onSuccess(res);
      setErrorMessage('');
      return;
    },
    [limit, multiple, uploadedFiles, onSuccess, dispatch, type, endpoint, addMessage],
  );

  const onRemovePreview = (f: UploadResponse) => {
    setUploadedFiles(uploadedFiles.filter(file => file.accessUrl !== f.accessUrl));
    if (onRemove) onRemove(f);
  };

  const renderPreview = () => {
    if (!preview || !uploadedFiles || uploadedFiles.length === 0) {
      return null;
    }
    return (
      <Row flexDirection="row" flexWrap="wrap" mt={3}>
        {uploadedFiles?.map((f: UploadResponse, i) => {
          if (!f.fileType.includes('image')) {
            return null;
          }
          return (
            <UiImage
              boxSize="100px"
              url={useLocalUrl ? f.localUrl : f.accessUrl}
              containerClasses="mb-2"
              key={`image-index-${f.accessUrl}`}
              onClose={() => onRemovePreview(f)}
              mr="2"
              objectFit="cover"
            />
          );
        })}
      </Row>
    );
  };

  const renderPlaceholder = () => {
    if (placeholder === null) {
      return null;
    }
    return (
      <UiText fontSize="md" fontWeight="semibold">
        {placeholder}
      </UiText>
    );
  };

  return (
    <UiBox cursor="pointer">
      <Dropzone onDrop={onDrop} accept={getAcceptFileTypes(acceptType)}>
        {({getRootProps, getInputProps}) => (
          <div {...getRootProps()}>
            <UiCenter borderWidth={1} {...{...defaultContentProps, ...contentProps}}>
              {loading && <UiSpinner size="lg" />}
              <UiIcon as={FiUpload} boxSize={'25px'} />
              {renderPlaceholder()}
              <input {...getInputProps()} />
            </UiCenter>
          </div>
        )}
      </Dropzone>
      {renderPreview()}
      {errorMessage && (
        <UiCenter>
          <UiText fontSize="xs" color="red" mt={1}>
            {errorMessage}
          </UiText>
        </UiCenter>
      )}
    </UiBox>
  );
});
